import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';
import {
  type OnFarmClientOnFarmEmbryo,
  onFarmClientOnFarmMatingEmbryosOptions,
  toRecordKey,
} from '@/services/on-farm-implant';

import type { MatingRecord } from '../../types';

export const CLIENT_ON_FARM_SOURCE_ID = 'client-on-farm-tog';

export const toEmbryoRecordKey = (embryo: OnFarmClientOnFarmEmbryo) =>
  toRecordKey(embryo.embryoId, embryo.accountId);

export type ClientOnFarmSelectionValue = {
  selectedKeys: Set<string>;
  visibleKeys?: Set<string>;
  search?: string;
  readOnly: boolean;
  setEmbryosSelected: (keys: string[], selected: boolean) => void;
  loadMatingEmbryoKeys: (mating: MatingRecord) => Promise<string[]>;
};

const ClientOnFarmSelectionContext =
  createContext<ClientOnFarmSelectionValue | null>(null);

export const ClientOnFarmSelectionProvider =
  ClientOnFarmSelectionContext.Provider;

export const useClientOnFarmSelectionContext = () => {
  const value = useContext(ClientOnFarmSelectionContext);
  if (!value) {
    throw new Error(
      'useClientOnFarmSelectionContext must be used inside ClientOnFarmSelectionProvider',
    );
  }
  return value;
};

export const matingEmbryosOptions = (
  token: AccessToken,
  mating: Pick<MatingRecord, 'id' | 'accountId'>,
  search?: string,
) =>
  onFarmClientOnFarmMatingEmbryosOptions(token, {
    matingId: mating.id,
    accountId: mating.accountId,
    search,
  });

export const useClientOnFarmSelection = ({
  sourceId,
  selectedRecordKeys,
  onSelectedRecordKeysChange,
  search,
  visibleKeys,
  readOnly,
}: {
  sourceId: string;
  selectedRecordKeys: string[];
  onSelectedRecordKeysChange: (sourceId: string, recordKeys: string[]) => void;
  search?: string;
  visibleKeys?: Set<string>;
  readOnly: boolean;
}): ClientOnFarmSelectionValue => {
  const token = useAccessToken();
  const queryClient = useQueryClient();
  const selectedRef = useRef(selectedRecordKeys);

  useEffect(() => {
    selectedRef.current = selectedRecordKeys;
  }, [selectedRecordKeys]);

  const selectedKeys = useMemo(
    () => new Set(selectedRecordKeys),
    [selectedRecordKeys],
  );

  const setEmbryosSelected = useCallback(
    (keys: string[], selected: boolean) => {
      const current = selectedRef.current;
      const next = selected
        ? [...current, ...keys.filter((key) => !current.includes(key))]
        : current.filter((key) => !keys.includes(key));
      selectedRef.current = next;
      onSelectedRecordKeysChange(sourceId, next);
    },
    [onSelectedRecordKeysChange, sourceId],
  );

  const loadMatingEmbryoKeys = useCallback(
    async (mating: MatingRecord) => {
      const embryos = await queryClient.fetchQuery(
        matingEmbryosOptions(token, mating, search),
      );
      const keys = embryos.map(toEmbryoRecordKey);
      return visibleKeys ? keys.filter((key) => visibleKeys.has(key)) : keys;
    },
    [queryClient, search, token, visibleKeys],
  );

  return useMemo(
    () => ({
      selectedKeys,
      visibleKeys,
      search,
      readOnly,
      setEmbryosSelected,
      loadMatingEmbryoKeys,
    }),
    [
      loadMatingEmbryoKeys,
      readOnly,
      search,
      selectedKeys,
      setEmbryosSelected,
      visibleKeys,
    ],
  );
};
